import React, { useCallback, useEffect, useMemo } from 'react';
import {
	IGetDishesByRestaurantIdsAndSubcategoryIds,
	IDishesByRestaurantIdsAndSubcategoryIdsItem,
	IGetDishesByRestaurantIdsAndSubcategoryIdsArgs,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishesByRestaurantIdsAndSubcategoryIds } from 'backend/dishes';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './DishesMultipleSelect.module.scss';

interface IDishesMultipleSelect {
	setIDsDishes: (value: string[]) => void;
	setFetchedDishes: (value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => void;
	updateDishes: (value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => void;
	IDsSelectedDishes: string[];
	IDsSelectedSubcategories: string[];
	IDsSelectedRestaurant: string[];
}

const DishesMultipleSelect: React.FC<IDishesMultipleSelect> = ({
	setIDsDishes,
	setFetchedDishes,
	updateDishes,
	IDsSelectedDishes,
	IDsSelectedSubcategories,
	IDsSelectedRestaurant,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<
		IGetDishesByRestaurantIdsAndSubcategoryIds,
		IGetDishesByRestaurantIdsAndSubcategoryIdsArgs
	>(
		IDsSelectedRestaurant.length && IDsSelectedSubcategories.length
			? getDishesByRestaurantIdsAndSubcategoryIds
			: null,
		{
			restaurantIds: IDsSelectedRestaurant,
			subCategoryIds: IDsSelectedSubcategories,
		},
	);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedDishes.filter((el) => el !== id);
			setIDsDishes(updatedSelectedItems);
		},
		[IDsSelectedDishes, setIDsDishes],
	);

	const dishesList = useMemo(() => {
		return IDsSelectedDishes.map((el) => {
			const dish = response?.data.dishes.find((item) => item.id === el);

			return (
				<SelectedItem
					key={el}
					label={dish?.name || ''}
					onDelete={() => handleRemove(el)}
					additionalContent={
						<>
							{dish?.externalId && (
								<ChunkOfTextWithCopying text={dish.externalId} className={style.externalId} />
							)}
						</>
					}
				/>
			);
		});
	}, [IDsSelectedDishes, response, handleRemove]);

	useEffect(() => {
		if (IDsSelectedSubcategories.length) {
			refetch();
		} else {
			setIDsDishes([]);
		}
	}, [IDsSelectedSubcategories]);

	useEffect(() => {
		if (response) {
			setFetchedDishes(response.data.dishes);
			updateDishes(response.data.dishes);
		}
	}, [response]);

	return (
		<div className={style.container} data-test="DishesMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.dishes || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					IDsSelectedSubcategories={IDsSelectedSubcategories}
					IDsSelectedDishes={IDsSelectedDishes}
					setIDsDishes={setIDsDishes}
				/>
			</div>
			<div className={style.groupsList}>{dishesList}</div>
		</div>
	);
};

export default React.memo(DishesMultipleSelect);
